import { Auth } from 'aws-amplify';
import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { IAuth, EMPTY_AUTH } from '../store/types/auth';
import { buildPermissions, roleLabel } from '../auth/permissions';

/** Tokens younger than this are not refreshed again (avoids a second round-trip right after sign-in). */
const FRESH_TOKEN_AGE_S = 60;

/** The refresh token was rejected (revoked, expired, rotated elsewhere). */
export const isNotAuthorizedError = (error: any): boolean =>
    error?.code === 'NotAuthorizedException' || error?.name === 'NotAuthorizedException';

/** Amplify has no signed-in user at all (signed out in another tab, storage cleared). */
export const isNoSessionError = (error: any): boolean =>
    /not authenticated|no current user/i.test(typeof error === 'string' ? error : error?.message ?? '');

const refreshUserSession = (user: CognitoUser): Promise<CognitoUserSession> =>
    new Promise((resolve, reject) => {
        const current = user.getSignInUserSession();
        if (!current) {
            reject(new Error('No Cognito session'));
            return;
        }
        user.refreshSession(current.getRefreshToken(), (err: any, session: CognitoUserSession) =>
            err ? reject(err) : resolve(session)
        );
    });

let inflightRefresh: Promise<CognitoUserSession> | null = null;
/** One Cognito refresh at a time: parallel callers (several 401s, SessionKeeper) share the same request. */
const refreshUserSessionOnce = (user: CognitoUser): Promise<CognitoUserSession> => {
    if (!inflightRefresh) {
        inflightRefresh = refreshUserSession(user).finally(() => {
            inflightRefresh = null;
        });
    }
    return inflightRefresh;
};

/**
 * The signed-in user's session. With `forceRefresh` new tokens are requested from Cognito so the
 * `cognito:groups` claim reflects the current group membership (skipped for tokens younger than
 * `minTokenAgeSeconds`; pass 0 to always refresh). When Cognito cannot be reached the cached
 * session is kept; a rejected refresh token propagates as NotAuthorizedException.
 */
export const getCurrentSession = async (
    { forceRefresh = false, minTokenAgeSeconds = FRESH_TOKEN_AGE_S }: { forceRefresh?: boolean; minTokenAgeSeconds?: number } = {}
): Promise<{ user: CognitoUser; session: CognitoUserSession }> => {
    const user: CognitoUser = await Auth.currentAuthenticatedUser();
    let session: CognitoUserSession = user.getSignInUserSession() ?? (await Auth.currentSession());
    if (forceRefresh && !isFreshSession(session, minTokenAgeSeconds)) {
        try {
            session = await refreshUserSessionOnce(user);
        } catch (error) {
            if (isNotAuthorizedError(error)) throw error;
            console.warn('[auth] Could not refresh the Cognito session, keeping the cached one', error);
        }
    }
    return { user, session };
};

const isFreshSession = (session: CognitoUserSession, minTokenAgeSeconds: number): boolean => {
    if (minTokenAgeSeconds <= 0) return false;
    const issuedAt: number = session.getIdToken().payload.iat ?? 0;
    // Cognito records how far the device clock was from the server when the tokens were issued.
    const clockDrift: number = typeof (session as any).getClockDrift === 'function' ? (session as any).getClockDrift() : 0;
    const ageSeconds = Date.now() / 1000 - clockDrift - issuedAt;
    return ageSeconds >= 0 && ageSeconds < minTokenAgeSeconds;
};

const toAuth = (user: CognitoUser, session: CognitoUserSession): IAuth => {
    const groups: string[] = session.getIdToken().payload['cognito:groups'] ?? [];
    return {
        name: user.getUsername(),
        token: session.getAccessToken().getJwtToken(),
        isLoggedIn: true,
        role: roleLabel(groups),
        allRoles: groups,
        permissions: buildPermissions(groups),
    };
};

export const getAuthInfo = async (options?: { forceRefresh?: boolean; minTokenAgeSeconds?: number }): Promise<IAuth> => {
    const { user, session } = await getCurrentSession(options);
    return toAuth(user, session);
};

// Per AWS Cognito/Amplify docs, `Auth.currentSession()` transparently refreshes the
// idToken/accessToken using the stored refreshToken whenever the cached session is
// expired (or close to it), and simply returns the still-valid cached session otherwise.
// Calling this right before an API request guarantees a non-expired accessToken without
// ever forcing a sign-out or a page reload.
export const getFreshAccessToken = async (): Promise<string> => {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
};

/** New tokens from Cognito regardless of age (apiFetch uses it after a 401, so no freshness shortcut). */
export const forceRefreshedAccessToken = async (): Promise<string> => {
    const { session } = await getCurrentSession({ forceRefresh: true, minTokenAgeSeconds: 0 });
    return session.getAccessToken().getJwtToken();
};

export const logOut = async (): Promise<IAuth> => {
    try {
        await Auth.signOut();
    } catch (error) {
        console.error('[auth] Sign-out failed', error);
    }
    return EMPTY_AUTH;
};
